import { styled, css, xStyledCommonProps } from '@elliemae/ds-system';
import { DSFormLayoutBlockItemSlots, DSFormLayoutBlockItemName } from './DSFormLayoutBlockItemDefinitions.js';
interface DSFormLayoutBlockItemStyledMessageT {
  hasError?: boolean;
  leftLabel?: boolean;
  hideLabel?: boolean;
}

interface DSFormLayoutBlockItemStyledContainerT {
  leftLabel?: boolean;
  fitContent?: boolean;
  hideLabel?: boolean;
  withHighlight?: boolean;
  isGroup?: boolean;
}

interface DSFormLayoutBlockItemStyledLabelT {
  leftLabel?: boolean;
  hideLabel?: boolean;
}

interface DSFormLayoutBlockItemStyledMarkT {
  required?: boolean;
}

interface DSFormLayoutBlockItemStyledCharCounterT {
  hasError?: boolean;
}

export const StyledContainer = styled('div', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.CONTAINER,
})<DSFormLayoutBlockItemStyledContainerT>`
  position: relative;
  padding: 0;
  margin: 0;
  display: grid;
  ${xStyledCommonProps}
  ${({ fitContent }) => (fitContent ? 'width: fit-content;' : undefined)}
  ${({ isGroup }) => (isGroup ? 'border: none' : '')};
  ${({ leftLabel, hideLabel }) => (leftLabel && !hideLabel ? 'grid-template-columns: auto 1fr;' : undefined)}
  align-items: center;
  ${({ theme, withHighlight }) =>
    withHighlight
      ? `
  &::after {
        position: absolute;
        content: '';
        left: -${theme.space.xxs};
        top: -${theme.space.xxxs};
        z-index: -1;
        width: calc(100% + ${theme.space.xs});
        height: calc(100%  + ${theme.space.xxs});
        background-color: #FEFBEE;
        border : 1px solid #8F6326;
        border-radius : 4px;
        box-shadow: 0 1px 3px 0 rgba(0,0,0,0.35);
        }
        &:hover, 
        &:focus-within {
          &::after {
            position: absolute;
            content: '';
            left: -${theme.space.xxs};
            top: -${theme.space.xxxs};
            z-index: -1;
            width: calc(100% + ${theme.space.xs});
            height: calc(100%  + ${theme.space.xxs});
            background-color: #FFF9D3;
            border : 2px solid #8F6326;
            border-radius : 4px;
            box-shadow: 0 6px 10px 0 rgba(0,0,0,0.30);
          }
        }
  `
      : undefined}
`;

export const StyledLabel = styled('label', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.LABEL,
})<DSFormLayoutBlockItemStyledLabelT>`
  display: flex;
  color: ${({ theme }) => theme.colors.neutral[700]};
  ${({ theme, leftLabel }) => (leftLabel ? `padding-right: ${theme.space.xxxs};` : undefined)}
  ${({ hideLabel }) =>
    hideLabel
      ? `position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
  `
      : undefined}
`;

const messageCss = css<DSFormLayoutBlockItemStyledMessageT>`
  font-size: ${({ theme }) => theme.fontSizes.microText[200]};
  text-align: right;
  line-height: 1.231rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.small}) {
    line-height: 1rem;
  }
  min-height: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme, hasError }) => (hasError ? theme.colors.danger[900] : theme.colors.neutral[500])};
  ${({ leftLabel, hideLabel }) => (leftLabel && !hideLabel ? 'grid-column: 1/3;' : undefined)}
  font-style: normal;
`;

export const StyledMessage = styled('div', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.MESSAGE,
})<DSFormLayoutBlockItemStyledMessageT>`
  ${messageCss}
`;

export const StyledErrorMessage = styled('div', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.ERROR_MESSAGE,
})<DSFormLayoutBlockItemStyledMessageT>`
  ${messageCss}
`;

export const StyledMark = styled('span', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.MARK,
})<DSFormLayoutBlockItemStyledMarkT>`
  margin-left: ${({ theme }) => theme.space.xxxs};
  ${({ theme, required }) =>
    required
      ? `
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1rem;
  font-size: 1.5rem;
  :after {
    content: '*';
    color: ${theme.colors.danger[900]};
  }
  `
      : `
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: 1px solid ${theme.colors.brand[600]};
  `}
`;

export const ScreenReaderOnly = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const StyledCharCount = styled('span', {
  name: DSFormLayoutBlockItemName,
  slot: DSFormLayoutBlockItemSlots.COUNTER,
})<DSFormLayoutBlockItemStyledCharCounterT>`
  justify-self: flex-end;
  margin-left: 16px;
  color: ${({ theme, hasError }) => (hasError ? theme.colors.danger[900] : theme.colors.neutral[500])};
`;
